import { useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { Button, Flex, Message, Notification, theme, useMessagedAsyncFn } from "@payping/web-components";
import { FC, useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";

interface DrawProps {
  goBack: () => void;
  onFinish: () => void;
}
const BnplActivationPageFormSignatureDraw: FC<DrawProps> = ({ goBack, onFinish }) => {
  const { t } = useAppTranslation();

  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const signaturePadRef = useRef<SignaturePad>(null);

  const { token } = theme.useToken();
  const [notification, contextHolder] = Notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  const [uploadSignatureState, setUploadSignature] = useMessagedAsyncFn(
    async (signature: File) => {
      try {
        const res = await bnplMerchantApis.uploadSignatureImage(signature, { isRaw: true });
        onFinish();
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400 && (jsonError.key === 106 || jsonError.key === 107)) {
              if (typeof jsonError?.detail === "string") {
                throw new Error(jsonError.detail);
              }
              throw jsonError;
            }
            throw e;
          }
        }
        throw e;
      }
    },
    [onFinish],
    { loading: false },
    { error: true },
  );

  useEffect(() => {
    const signaturePad = signaturePadRef.current;
    const canvas = canvasRef;
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);
      (signaturePadRef as any).current = new SignaturePad(canvas, { backgroundColor: "#ffffff", penColor: "#000000" });
    }
    return () => {
      if (signaturePad) {
        signaturePad.clear();
        signaturePad.off();
      }
    };
  }, [canvasRef]);

  const clearDraw = () => {
    const signaturePad = signaturePadRef.current;
    if (signaturePad) {
      signaturePad.clear();
    }
  };

  async function confirmDraw() {
    const maxSize = 50 * 1024;
    const signaturePad = signaturePadRef.current;

    if (signaturePad) {
      if (signaturePad.isEmpty()) {
        notification.error({
          message: String(t("shop.bnpl.activation.formDrawer.steps.signature.draw.errorForDraw")),
          closable: true,
        });
      }
      // continue if draw
      else {
        const data = signaturePad.toDataURL("image/jpeg");
        const blob = await (await fetch(data)).blob();

        if (maxSize && blob.size >= maxSize) {
          Message.error({ content: t("shop.bnpl.activation.formDrawer.steps.signature.draw.sizeError") });
        } else {
          await setUploadSignature(new File([blob], "signature.jpeg", { type: "image/jpeg" }));
          onFinish();
        }
      }
    }
  }

  return (
    <>
      {contextHolder}
      <Flex vertical gap={"small"}>
        <canvas
          className="tw-h-[40dvh] tw-w-full tw-rounded-lg"
          style={{ border: "1px solid", borderColor: token.colorBorder }}
          ref={(ref) => setCanvasRef(ref)}
        ></canvas>
        <Button
          onClick={() => {
            clearDraw();
          }}
        >
          {t("shop.bnpl.activation.formDrawer.steps.signature.draw.redraw")}
        </Button>
      </Flex>
      <Flex gap={"middle"}>
        <Button type="default" onClick={goBack} disabled={uploadSignatureState.loading}>
          {t("shop.bnpl.activation.formDrawer.previousStep")}
        </Button>
        <Button loading={uploadSignatureState.loading} onClick={confirmDraw} type="primary">
          {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
        </Button>
      </Flex>
    </>
  );
};

export default BnplActivationPageFormSignatureDraw;
